// 📄 pages/Proyectos/components/EspecificacionTecnicaCard.tsx
import React from 'react';
import { Pencil, FileText, Users } from 'lucide-react';
import {
  EspecificacionTecnica, EspecificacionEstado, ESPECIFICACION_ESTADOS
} from '../../../services/especificacionTecnicaService.ts';
import { SOLUCIONES } from '../solucionesConfig.ts';

const ESTADO_BADGE: Record<EspecificacionEstado, string> = {
  'Pendiente': 'bg-blue-500/10 text-blue-300 border-blue-500/20',
  'Finalizado': 'bg-emerald-500/10 text-emerald-300 border-emerald-500/20',
  'Cancelado': 'bg-white/5 text-gray-400 border-white/10'
};

const PRIORIDAD_BADGE: Record<string, string> = {
  'Alta': 'bg-red-500/10 text-red-300 border-red-500/20',
  'Media': 'bg-amber-500/10 text-amber-300 border-amber-500/20',
  'Baja': 'bg-white/5 text-gray-400 border-white/10'
};

const AREA_BADGE: Record<string, string> = {
  'Funcional': 'bg-sky-500/10 text-sky-300 border-sky-500/20',
  'Técnica': 'bg-purple-500/10 text-purple-300 border-purple-500/20'
};

interface EspecificacionTecnicaCardProps {
  especificacion: EspecificacionTecnica;
  puedeEditar: boolean;
  onEditar: () => void;
  onEstadoChange: (estado: EspecificacionEstado) => void;
}

export default function EspecificacionTecnicaCard({ especificacion: e, puedeEditar, onEditar, onEstadoChange }: EspecificacionTecnicaCardProps) {
  const adjuntos = e.adjuntos || [];

  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h4 className="text-sm font-semibold text-white">{e.titulo}</h4>
          <p className="text-xs text-gray-500 mt-0.5">
            {SOLUCIONES[e.solucionRelacionada]?.label || e.solucionRelacionada}
            {e.canalNombre ? ` · ${e.canalNombre}` : ''}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className={`text-xs px-2 py-0.5 rounded-full border ${ESTADO_BADGE[e.estado]}`}>{e.estado}</span>
          {puedeEditar && (
            <button onClick={onEditar} className="text-gray-400 hover:text-white">
              <Pencil className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5">
        <span className={`text-xs px-2 py-0.5 rounded-full border ${PRIORIDAD_BADGE[e.prioridad]}`}>Prioridad {e.prioridad}</span>
        <span className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border ${AREA_BADGE[e.areaResponsable]}`}>
          <Users className="w-3 h-3" /> Turno: {e.areaResponsable}
        </span>
      </div>

      {e.descripcion && <p className="text-sm text-gray-300 whitespace-pre-wrap">{e.descripcion}</p>}

      {adjuntos.length > 0 && (
        <div className="space-y-1.5">
          {adjuntos.map((a) => (
            <a
              key={a.url} href={a.url} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-2 bg-black/20 border border-white/10 rounded-lg px-3 py-1.5 text-xs text-gray-300 hover:border-blue-400 transition-all"
            >
              <FileText className="w-3.5 h-3.5 text-blue-400 flex-shrink-0" />
              <span className="truncate">{a.name}</span>
            </a>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between gap-3 pt-1 border-t border-white/10">
        <p className="text-xs text-gray-500">{e.creadoPorNombre}</p>
        {puedeEditar && (
          <select
            value={e.estado}
            onChange={(ev) => onEstadoChange(ev.target.value as EspecificacionEstado)}
            className="bg-zinc-800 border border-white/10 rounded-lg px-2 py-1 text-xs text-white focus:outline-none focus:border-blue-500"
          >
            {ESPECIFICACION_ESTADOS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        )}
      </div>
    </div>
  );
}
